import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Button from './Button';
import './CollapsibleActions.css';

// SECONDARY HEADER ACTIONS, FOLDED BEHIND A CHEVRON.
//
// A page header carrying every action as a full-size button crowds out the thing it is a header for — on the sync
// project page the project's own name was cut to "Hilton Vallarta Riviera All-In…" to make room for seven buttons.
// The actions used every visit (a status switch, Refresh) stay in the row; the rest sit behind one "More" toggle and
// open INLINE beside it, the same buttons in the same order, so nothing moves into an unfamiliar menu.
//
// Remembered per screen (storageKey) in this browser only — a convenience, so somebody who always wants them open
// is not asked again. Storage can be unavailable (private window, blocked site data); the page then starts closed.
//
// Props:
//   children     the buttons to fold away
//   storageKey   e.g. 'sync-project-header' — omit to start closed every time
//   label        the toggle's text (default "More")
const readOpen = (key) => {
    if (!key) return false;
    try { return window.localStorage.getItem(`hc-actions:${key}`) === 'open'; } catch (e) { return false; }
};
const writeOpen = (key, open) => {
    if (!key) return;
    try { window.localStorage.setItem(`hc-actions:${key}`, open ? 'open' : 'closed'); } catch (e) { /* not remembered */ }
};

const CollapsibleActions = ({ children, storageKey, label = 'More' }) => {
    const [open, setOpen] = useState(() => readOpen(storageKey));
    const panelId = useId();
    const toggle = () => setOpen(v => { writeOpen(storageKey, !v); return !v; });

    return (
        <div className="hc-collapsible-actions">
            {open && <div id={panelId} className="hc-collapsible-actions-panel">{children}</div>}
            <Button variant="secondary" onClick={toggle} aria-expanded={open} aria-controls={panelId}
                    title={open ? 'Hide these actions' : 'Show more actions'}>
                {open ? 'Less' : label}
                <ChevronDown className={`icon hc-collapsible-actions-chevron${open ? ' is-open' : ''}`} />
            </Button>
        </div>
    );
};

export default CollapsibleActions;
